import { useEffect, useState } from "react";
import { PiSquare, PiCheckSquare } from "react-icons/pi";
import { IPhenomenons } from "../types/phenomenon_type";

interface PhenomenonAllListProps {
  phenomenons: IPhenomenons;
  phenomenonNumber: number;
  setSelectedPhenomenonGroup: React.Dispatch<React.SetStateAction<boolean[]>>;
  setSelectedPhenomenonName: React.Dispatch<React.SetStateAction<string[]>>;
}

/**
 * 現象リストの単一選択メニュー・アコーディオンメニューを表示するコンポーネント
 * @param phenomenons - 現象リスト
 * @param phenomenonNumber - 取得した現象リストの何番目の現象グループか
 * @param setSelectedPhenomenonGroup - 現象グループ全体のチェック状態を更新する関数
 * @param setSelectedPhenomenonName - 現象グループ名 または 現象項目名を入れた配列を更新する関数
 * @returns　JSX.Element - 各種メニューの表示
 */
export const PhenomenonAllList: React.FC<PhenomenonAllListProps> = ({
  phenomenons,
  phenomenonNumber,
  setSelectedPhenomenonGroup,
  setSelectedPhenomenonName,
}) => {
  //アコーディオンメニューかどうか判定, question2が空配列(lengthが0)の場合false, そうでない場合true
  const isAccordion: boolean = !!phenomenons.question1.question2.length;
  // 現象グループのどの項目が選択されたかを格納
  const [selectedPhenomenonItem, setSelectedPhenomenonItem] = useState<
    boolean[]
  >(isAccordion ? phenomenons.question1.question2.map(() => false) : [false]);

  // 現象項目の選択状態が変化した際の処理
  useEffect(() => {
    setSelectedPhenomenonGroup((selectedPhenomenonGroup) =>
      selectedPhenomenonItem.some((value) => value)
        ? // 現象項目が1つでも選択されていれば, その現象グループの選択状態をtrueにする
          selectedPhenomenonGroup.map((v, i) =>
            i === phenomenonNumber ? true : v
          )
        : // 現象項目が選択されていなければ, その現象グループの選択状態をfalseにする
          selectedPhenomenonGroup.map((v, i) =>
            i === phenomenonNumber ? false : v
          )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhenomenonItem]);

  // 現象項目 or 現象グループ(非アコーディオン時)のチェックボックスをクリックした際の処理
  const handleItemSelect = (phenomenon: string, index: number) => {
    setSelectedPhenomenonItem((selectedPhenomenonItem) =>
      selectedPhenomenonItem.map((v, i) =>
        i === index ? !selectedPhenomenonItem[index] : v
      )
    );
    setSelectedPhenomenonName((selectedPhenomenonName: string[]) => {
      return selectedPhenomenonName.indexOf(phenomenon) === -1
        ? // 現象名のstring配列に選択した現象項目が入っていなければ, その現象項目名を追加する
          [...selectedPhenomenonName, phenomenon]
        : // 現象項目が入っていれば, その現象項目を除いた配列を生成する
          selectedPhenomenonName.filter(
            (phenomenonName) => phenomenonName !== phenomenon
          );
    });
  };

  // 現象項目リスト(2階層目)をレンダリングする関数
  const renderPhenomenonItems = () => {
    // アコーディオンメニューの場合のみリスト生成処理を実行
    return isAccordion ? (
      phenomenons.question1.question2.map((question2, index) => {
        return (
          <dd className="contents" key={question2.label}>
            <div
              className={`flex items-center ${
                index + 1 !== phenomenons.question1.question2.length &&
                "border-b"
              } border-gray-700 py-2 pr-3 ${
                selectedPhenomenonItem[index] && "bg-sub-orange"
              }`}
            >
              <div
                className="px-3 cursor-pointer"
                onClick={() => handleItemSelect(question2.label, index)}
              >
                {selectedPhenomenonItem[index] ? (
                  <PiCheckSquare size={"1.25rem"} />
                ) : (
                  <PiSquare size={"1.25rem"} />
                )}
              </div>
              {/* <div className="line-clamp-2">{question2.label}</div> */}
              <div>{question2.label}</div>
            </div>
          </dd>
        );
      })
    ) : (
      <></>
    );
  };

  // 現象グループ(1階層目)をレンダリングする関数
  const renderPhenomenonGroup = () => {
    //アコーディオンメニューではない時の配列の要素番号を指定する定数
    const NOT_ACCORDION_INDEX = 0;

    return isAccordion ? (
      //アコーディオンメニューであるときの処理
      <dt
        className={`flex items-center border-b border-gray-700 cursor-pointer py-2 px-3 ${
          selectedPhenomenonItem.some((value) => value) && "bg-sub-orange"
        }`}
      >
        {/* <div className="pr-3">{open ? <FaMinus /> : <FaPlus />}</div> */}
        {/* <div className="line-clamp-2">{phenomenons.question1.label}</div> */}
        <div>{phenomenons.question1.label}</div>
      </dt>
    ) : (
      //アコーディオンメニューではない時の処理
      <dt
        className={`flex items-center border-gray-700 cursor-pointer py-2 px-3 ${
          selectedPhenomenonItem[NOT_ACCORDION_INDEX] && "bg-sub-orange"
        }`}
      >
        <div
          className="pr-3"
          onClick={() =>
            handleItemSelect(phenomenons.question1.label, NOT_ACCORDION_INDEX)
          }
        >
          {selectedPhenomenonItem[NOT_ACCORDION_INDEX] ? (
            <PiCheckSquare size={"1.25rem"} />
          ) : (
            <PiSquare size={"1.25rem"} />
          )}
        </div>
        {/* <div className="line-clamp-2">{phenomenons.question1.label}</div> */}
        <div>{phenomenons.question1.label}</div>
      </dt>
    );
  };

  // 各リストをレンダリングする関数
  const renderList = () => {
    return (
      <div>
        {renderPhenomenonGroup()}
        {renderPhenomenonItems()}
      </div>
    );
  };

  return (
    <dl className="bg-white rounded border border-gray-700 mb-4">
      {renderList()}
    </dl>
  );
};
